import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

import { getCurrentMilestone } from 'lib/utils';
import { PATH_ROUTES } from 'routes/path';
import {
  getAccountStatus,
  getOnboarding,
  getOnboardingSteps,
} from 'services/dashboard/api';
import useAuthStore from 'store/auth';
import { captureUserActivity, getMemberDetails } from 'services/profile/api';
import { USER_ACTIVITY } from '../types';
import { AccountStatus, OnboardingStep } from 'services/dashboard/type';
import { useLogin } from 'services/auth/hook';
import { signOut } from 'services/auth/api';
import { LoginResponse } from 'services/auth/type';

interface AuthenticateProps {
  email: string;
  password: string;
  setLoading: (loading: boolean) => void;
  isLogin?: boolean;
}

export default function useAuthenticate() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    setIsAuthenticated,
    setOnBoardingSteps,
    setAccountStatus,
    setCurrentMilestone,
    setUserEmail,
    setMemberDetails,
    setCurrentUserProfileId,
    setAdminProfileId,
    setOnBoarding,
    setSignupEmail,
    setSignupPassword,
    setTokenGeneratedTime,
    setTokenExpiryTime,
    setSignupPhone,
    setSignupUserId,
    setEmailVerified,
    setPhoneVerified,
  } = useAuthStore();

  const { mutateAsync } = useLogin();

  const onUpdateMemberDetails = async () => {
    let adminProfileId = '';
    const memberDetails = await getMemberDetails();
    if (memberDetails && memberDetails?.length) {
      setMemberDetails(memberDetails);
      adminProfileId = memberDetails.find(
        (member) => member.relation === 'Admin'
      )!.profile_id;
      setCurrentUserProfileId(adminProfileId);
      setAdminProfileId(adminProfileId);
    }

    return adminProfileId;
  };

  const setUserDetails = async ({
    email,
    setLoading,
    isLogin,
  }: Omit<AuthenticateProps, 'password'>) => {
    try {
      let requests: Promise<unknown>[] = [
        getAccountStatus(),
        getOnboardingSteps(),
        getOnboarding(),
      ];

      const res = await Promise.all(requests);

      const status = res[0] as AccountStatus;
      setAccountStatus(status);

      const steps = res[1] as AxiosResponse<OnboardingStep[]>;
      setOnBoardingSteps(steps?.data ?? []);

      const onboarding = res[2] as AxiosResponse<OnboardingStep[]>;
      setOnBoarding(onboarding?.data || []);
      if (onboarding?.data?.length) {
        const currentOnboarding = getCurrentMilestone(
          // @ts-ignore
          onboarding.data.at(-1)
        );
        setCurrentMilestone(currentOnboarding.name);
      } else {
        setCurrentMilestone(steps.data[0].name);
      }

      setUserEmail(email);

      setLoading(false);
      toast.success(t('login_message'));

      if (isLogin) captureUserActivity(USER_ACTIVITY.LOGIN);

      setIsAuthenticated(true);
      navigate(PATH_ROUTES.DASHBOARD);
    } catch (error) {
      signOut();
      setLoading(false);
      toast.error(t('login_failed_msg'));
    }
  };

  const handleLoginResponse = async ({
    res,
    password,
  }: {
    res: LoginResponse;
    password?: string;
  }) => {
    if (!res?.phone_verification_flag || !res?.email_verification_flag) {
      toast.error(t('verify_details_msg'));
      setSignupEmail(res?.user_name || '');
      setSignupPassword(password || '');
      setSignupPhone(res?.phone_number || '');
      setSignupUserId(res?.user_id || '');
      setEmailVerified(res?.email_verification_flag || false);
      setPhoneVerified(res?.phone_verification_flag || false);
      navigate(PATH_ROUTES.SIGNUP_VERIFICATION);
      throw new Error('User not verified');
    }

    setTokenGeneratedTime(res.generation_time);
    setTokenExpiryTime(res.expiry_time);
  };

  const onAuthenticate = async ({
    email,
    password,
    setLoading,
    isLogin,
  }: AuthenticateProps) => {
    try {
      const res = await mutateAsync({
        username: email.toLowerCase(),
        password,
      });

      await handleLoginResponse({ res, password });
    } catch (error) {
      console.log(error);
      if (error instanceof AxiosError) {
        setLoading(false);

        const res = error.response;
        if (res?.data?.challenge_name === 'NEW_PASSWORD_REQUIRED') {
          return navigate(PATH_ROUTES.RESET_PASSWORD, {
            state: {
              email: email.toLowerCase(),
              session: res.data?.session || '',
            },
          });
        }

        toast.error(error?.response?.data?.error);
      }
      return;
    }

    setUserDetails({ email, setLoading, isLogin });
  };

  return {
    onAuthenticate,
    onUpdateMemberDetails,
    setUserDetails,
    handleLoginResponse,
  };
}
